import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import { useGetDataSplitSummaryQuery } from '../../api/createProjectApiSlice';
import {
  setDefaultAllocationMode,
  clearDefaultAllocationMode,
} from '../../state/createProjectSlice';
import {
  selectDefaultAllocationMode,
  selectSelectedAnnotators,
  selectSelectedSupervisors,
  selectWorkflowId,
} from '../../state/selector';

interface UsePeopleAllocationProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

interface UsePeopleAllocationResults {
  defaultAllocationMode: boolean;
  setAllocationModeDefault: () => void;
  setAllocationModeCustom: () => void;
}
export const usePeopleAllocation = ({
  setRequiredFieldsFilled,
}: UsePeopleAllocationProps): UsePeopleAllocationResults => {
  const dispatch = useAppDispatch();
  const defaultAllocationMode = useAppSelector(selectDefaultAllocationMode);
  const setAllocationModeDefault = () => {
    dispatch(setDefaultAllocationMode());
  };
  const setAllocationModeCustom = () => {
    dispatch(clearDefaultAllocationMode());
  };

  const workflowId = useAppSelector(selectWorkflowId);
  const { data: dataSplitSummaryData } = useGetDataSplitSummaryQuery({
    workflowId: workflowId ?? '',
  });
  const dataSplitSummary = useMemo(
    () => dataSplitSummaryData?.data ?? { trainingCount: 0, testCount: 0, validationCount: 0 },
    [dataSplitSummaryData]
  );

  const selectedAnnotators = useAppSelector(selectSelectedAnnotators);
  const selectedSupervisors = useAppSelector(selectSelectedSupervisors);

  useEffect(() => {
    const trainingTotal = selectedAnnotators.reduce(
      (acc, annotator) => acc + (annotator.allocationPercentage?.trainingPercentage ?? 0),
      0
    );
    const testTotal = selectedAnnotators.reduce(
      (acc, annotator) => acc + (annotator.allocationPercentage?.testPercentage ?? 0),
      0
    );
    const validationTotal = selectedAnnotators.reduce(
      (acc, annotator) => acc + (annotator.allocationPercentage?.validationPercentage ?? 0),
      0
    );
    const allFieldsFilled = Boolean(
      selectedAnnotators.length > 0 &&
        selectedSupervisors.length > 0 &&
        (dataSplitSummary.trainingCount === 0 || trainingTotal === 100 || defaultAllocationMode) &&
        (dataSplitSummary.testCount === 0 || testTotal === 100 || defaultAllocationMode) &&
        (dataSplitSummary.validationCount === 0 || validationTotal === 100 || defaultAllocationMode)
    );
    setRequiredFieldsFilled(allFieldsFilled);
  }, [selectedAnnotators, selectedSupervisors, defaultAllocationMode, dataSplitSummary]);

  return {
    defaultAllocationMode,
    setAllocationModeDefault,
    setAllocationModeCustom,
  };
};
